// src/components/products/ProductForm/TransferDetails.jsx

import React, { useState } from "react";
import { Box, Typography, Paper, Accordion, AccordionSummary, AccordionDetails } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LanguageToggle from "../../global/LanguageToggle";

// ✅ Import Custom Hooks & Helpers
import useFormLanguage from "../../../hooks/forms/useFormLanguage";
import { handleMultiLangChange as multiLangHelper } from "../../../utils/forms/multiLangHelper";
import { createAccordionHandler, isPanelExpanded } from "../../../utils/forms/accordionHelper";

// Import Section Components
import TransferOperatingHoursSection from "./TransferDetailsSections/TransferOperatingHoursSection";
import TransferItinerarySection from "./TransferDetailsSections/TransferItinerarySection";
import TransferNotesSection from "./TransferDetailsSections/TransferNotesSection";

/**
 * Transfer Details Form Fields
 */
const TransferDetails = ({ formData, setFormData, errors = {} }) => {
    // ✅ استخدام Custom Hook للغة
    const { isArabic, activeLang, setActiveLang } = useFormLanguage();

    // ✅ Accordion state - يدعم فتح عدة أكورديونات
    const [expandedPanels, setExpandedPanels] = useState(['panel1']);
    const handleAccordionChange = createAccordionHandler(setExpandedPanels, true);

    // ✅ استخدام Multi-lang Helper
    const handleFieldChange = (field, lang, value) => {
        multiLangHelper(setFormData, formData, field, lang, value);
    };

    // Shared props for all sections
    const commonProps = {
        formData,
        setFormData,
        errors,
        isArabic,
        activeLang,
        handleMultiLangChange: handleFieldChange,
    };

    return (
        <Paper sx={{ p: 4 }}>
            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 4,
                }}
            >
                <Box>
                    <Typography variant="h6" sx={{ mb: 0.5, fontWeight: 600 }}>
                        {isArabic ? 'تفاصيل النقل (Transfer)' : 'Transfer Details'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {isArabic
                            ? 'أدخل تفاصيل خدمة النقل'
                            : 'Enter the transfer service details'}
                    </Typography>
                </Box>

                <LanguageToggle
                    activeLang={activeLang}
                    onChange={(lang) => setActiveLang(lang)}
                    showLabel={true}
                />
            </Box>

            {/* Accordions */}
            <Box>
                {/* 1. Operating Hours */}
                <Accordion
                    expanded={isPanelExpanded(expandedPanels, 'panel1')}
                    onChange={handleAccordionChange('panel1')}
                    sx={{ mb: 2 }}
                >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography sx={{ fontWeight: 600 }}>
                            {isArabic ? '⏰ أوقات التشغيل' : '⏰ Operating Hours'}
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <TransferOperatingHoursSection {...commonProps} />
                    </AccordionDetails>
                </Accordion>

                {/* 2. Itinerary */}
                <Accordion
                    expanded={isPanelExpanded(expandedPanels, 'panel2')}
                    onChange={handleAccordionChange('panel2')}
                    sx={{ mb: 2 }}
                >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography sx={{ fontWeight: 600 }}>
                            {isArabic ? '🗺️ خط سير الرحلة' : '🗺️ Itinerary'}
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <TransferItinerarySection {...commonProps} />
                    </AccordionDetails>
                </Accordion>

                {/* 3. Notes */}
                <Accordion
                    expanded={isPanelExpanded(expandedPanels, 'panel3')}
                    onChange={handleAccordionChange('panel3')}
                    sx={{ mb: 2 }}
                >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography sx={{ fontWeight: 600 }}>
                            {isArabic ? '📝 ملاحظات إضافية' : '📝 Additional Notes'}
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <TransferNotesSection {...commonProps} />
                    </AccordionDetails>
                </Accordion>
            </Box>
        </Paper>
    );
};

export default TransferDetails;
